import ListaEstaciones from '../estaciones/listaEstaciones'
import ListaVariables from '../variables/listaVariables'
import DatosVarsUI from './datosVarsUI'
import DateUtils from '../../utils/dateUtils'
import TypesUtils from '../../utils/typesUtils'
import metConstants from './metConstants'

class DatosVars {
    constructor(datosVarsJSON, datosVarsUI) {
        this.observaciones = datosVarsJSON.observaciones
        this.estaciones = new ListaEstaciones(datosVarsJSON.estaciones)
        this.variables = new ListaVariables(datosVarsJSON.variables)
        this.fechas = datosVarsJSON.fechas
        this.datosVarsUI = datosVarsUI ? datosVarsUI : new DatosVarsUI()
    }

    /**
     * Retorna true sii el indice dado es valido de variable
     */
    iVarValido(iVar) {
        return this.variables.validIndex(iVar)
    }

    /**
     * Retorna true sii el indice dado es valido de estacion
     */
    iEstacionValido(iEstacion) {
        return this.estaciones.validIndex(iEstacion)
    }

    /**
     * Retorna true sii el indice dado es valido de fecha para la variable dada
     */
    iFechaValido(iVar, iFecha) {
        return (
            this.iVarValido(iVar) &&
            iFecha >= 0 &&
            iFecha < this.observaciones[iVar].iFechas.length
        )
    }

    /**
     * Retorna true sii el indice dado es valido de fecha para la variable dada
     */
    iFechaGralValido(iFechaGral) {
        return iFechaGral >= 0 && iFechaGral < this.fechas.length
    }

    /**
     * Retorna el indice de la variable dado su id
     * @param {number} idVar
     */
    iVar(idVar) {
        return this.variables.indexOf(idVar)
    }

    /**
     * Retorna el indice de la estacion dado su id
     * @param {number} idEstacion
     */
    iEstacion(idEstacion) {
        return this.estaciones.indexOf(idEstacion)
    }

    /**
     * Retorna la variable del id dado
     *
     * @param {number} idVar
     */
    getVariable(idVar) {
        return this.variables.getById(idVar)
    }

    /**
     * Retorna la estación del id dado
     *
     * @param {number} idEstacion
     */
    getEstacion(idEstacion) {
        return this.estaciones.getById(idEstacion)
    }

    /**
     * Retorna la estación en el indice dado
     *
     * @param {number} iEstacion
     */
    getEstacionAt(iEstacion) {
        return this.estaciones.get(iEstacion)
    }

    /**
     * Retorna la variable en el indice dado
     *
     * @param {number} iVar
     */
    getVariableAt(iVar) {
        return this.variables.get(iVar)
    }

    getVars(ids) {
        return this.variables.filterByIds(ids)
    }

    getIdVar(iVar) {
        if (this.iVarValido(iVar)) return this.variables.get(iVar).id
        return null
    }

    /**
     * Retorna un array con los nombres de las estaciones tomando en cuenta los filtros y el orden actual.
     *
     */
    getNombresEstaciones() {
        return this.estaciones.getListaNombres()
    }

    /**
     * Retorna un array con los nombres de las variables tomando en cuenta los filtros y el orden actual.
     *
     */
    getNombresVariables(incluirUnidad) {
        return this.variables.getListaNombres(incluirUnidad)
    }

    getIdsVars() {
        return this.variables.getIds()
    }

    /**
     * Retorna los datos de la estacion y variable dados por sus indices
     *
     * @param {*} iVar Indice de la variable
     * @param {*} iEstacion Indice de la estacion
     */
    getDatos(iVar, iEstacion) {
        return this.observaciones[iVar].datos[iEstacion]
    }

    getFecha(iVar, iFecha) {
        if (this.iVarValido(iVar) && this.iFechaValido(iVar, iFecha))
            return this.fechas[this.observaciones[iVar].iFechas[iFecha]]
        return null
    }

    getIFechaGralDeIFecha(iVar, iFecha) {
        if (this.iFechaValido(iVar, iFecha))
            return this.observaciones[iVar].iFechas[iFecha]
        return null
    }

    getIFechaDeIFechaGral(iVar, iFechaGral) {
        let res = -1
        if (this.iVarValido(iVar) && this.iFechaGralValido(iFechaGral)) {
            this.observaciones[iVar].iFechas.find(
                (iFechaItem, indexIFechaItem) => {
                    if (iFechaItem === iFechaGral) {
                        res = indexIFechaItem
                        return true
                    }
                    return false
                }
            )
        }
        return res
    }

    /**
     * Retorna el dato correspondiente a los indices dados.
     * Si alguno de los indices es inválido returna null.
     * Si el dato no existe para los indices dados retorna null.
     * @param {number} iVar
     * @param {number} iEstacion
     * @param {number} iFecha
     */
    getDato(iVar, iEstacion, iFecha, usarOpcionesUI = true) {
        if (this.iVarValido(iVar) && this.iEstacionValido(iEstacion)) {
            if (!usarOpcionesUI)
                return this.observaciones[iVar].datos[iEstacion][iFecha]
            return this.datosVarsUI.getDatoPresentacion(
                this.getVariableAt(iVar),
                this.observaciones[iVar].datos[iEstacion][iFecha]
            )
        }
        return null
    }

    /**
     * Retorna el dato correspondiente a los ids dados y el indice de fecha.
     * Si alguno de los ids o el indice inválido returna null.
     * Si el dato no existe para los ids y el indice dados retorna null.
     * @param {number} iVar
     * @param {number} iEstacion
     * @param {number} iFecha
     */
    getDatoPorIds(idVar, idEstacion, iFecha) {
        let iVar = this.iVar(idVar)
        let iEstacion = this.iEstacion(idEstacion)
        if (this.iVarValido(iVar) && this.iEstacionValido(iEstacion)) {
            return this.datosVarsUI.getDatoPresentacion(
                this.getVariableAt(iVar),
                this.observaciones[iVar].datos[iEstacion][iFecha]
            )
        }
        return null
    }

    /**
     * Retorna un conjunto de datos de las variables dadas por sus ids en la estacion
     * y fechas dados por su id e indice respectivamente.
     *
     * Si alguno de los ids o el indice inválido returna null.
     * Si el dato no existe para los ids y el indice dados retorna null.
     * @param {number} idsVars
     * @param {number} iEstacion
     * @param {number} iFecha
     */
    getDatosPorIdsVars(idsVars, idEstacion, iFecha) {
        let iEstacion = this.iEstacion(idEstacion)
        let res = []
        idsVars.forEach((idVar) => {
            let iVar = this.iVar(idVar)
            if (this.iVarValido(iVar) && this.iEstacionValido(iEstacion)) {
                let dato = this.datosVarsUI.getDatoPresentacion(
                    this.getVariableAt(iVar),
                    this.observaciones[iVar].datos[iEstacion][iFecha]
                )
                if (!this.esNulo(dato)) {
                    res.push({
                        variable: this.variables.get(iVar),
                        dato,
                    })
                }
            }
        })
        return res
    }

    getValDatoNulo() {
        return this.datosVarsUI.getValDatoNulo()
    }

    esNulo(dato) {
        return dato === null || this.getValDatoNulo() === dato
    }

    getMaxIFechaGralDeVar(idVar) {
        let iVar = this.iVar(idVar)
        if (this.iVarValido(iVar)) {
            return this.observaciones[iVar].iFechas[
                this.observaciones[iVar].iFechas.length - 1
            ]
        }
        return -1
    }

    getCantIFechasDeVar(idVar) {
        let iVar = this.iVar(idVar)
        if (this.iVarValido(iVar)) {
            return this.observaciones[iVar].iFechas.length
        }
        return -1
    }

    // ------------------------------------
    // ----- Operaciones sobre fechas -----
    // ------------------------------------

    /**
     * Retorna un array de string de fechas de la variable dada por su id tomando en cuenta el tipo de repeticion.
     *
     * @param {*} idVar id de la variable
     * @param {*} tipoRepeticion Tipo de repeticion temporal
     */
    getArrayStrFechasDeVar(idVar, tipoRepeticion, ordenInversoFechas) {
        let res = []

        let iVar = this.iVar(idVar)
        if (iVar >= 0) {
            this.observaciones[iVar].iFechas.forEach((iFecha, i) => {
                let index = i
                if (ordenInversoFechas)
                    index = this.observaciones[iVar].iFechas.length - 1 - i
                let fecha = this.getFecha(iVar, index)

                res.push(
                    this.datosVarsUI.getFechaStrSegunRepeticion(
                        fecha,
                        tipoRepeticion
                    )
                )
            })
        }
        return res
    }

    /**
     * Retorna la cantidad de fechas de la variable dada por su id.
     *
     * @param {*} idVar id de la variable
     */
    getCantFechasDeVar(idVar) {
        let iVar = this.iVar(idVar)
        if (iVar >= 0) {
            return this.observaciones[iVar].iFechas.length
        }
        return -1
    }

    /**
     * Retorna un array de string de fechas de la variable dada por su id tomando en cuenta el tipo de repeticion.
     *
     * @param {*} idVar id de la variable
     * @param {*} tipoRepeticion Tipo de repeticion temporal
     */
    getArrayStrFechas(tipoRepeticion, ordenInversoFechas) {
        let res = []

        for (let i = 0; i < this.fechas.length; i++) {
            let index = i
            if (ordenInversoFechas) index = this.fechas.length - 1 - i
            let fechaAux = this.fechas[index]
            res.push(
                this.datosVarsUI.getFechaStrSegunRepeticion(
                    fechaAux,
                    tipoRepeticion
                )
            )
        }
        return res
    }

    getDatoVarNulo() {
        return this.datosVarsUI.datoVarNulo
    }

    // --------------------------------------------
    // ----- Operaciones de mapeo sobre datos -----
    // --------------------------------------------

    /**
     * Invoca la funcion mapFunction para cada dato de la variable dada por su id.
     * La fucion es invocada para cada estacion, y dentro de cada estacion para cada fecha.
     * Si se pasa una lista de estaciones en estacionesAMostrar solo se invocara para dichas estaciones
     * y se tomara el orden de dicha lista.
     *
     * @param {number} idVar
     * @param {requestCallback} mapFunction
     * @param {boolean} [ordenInversoFechas=false] Si es true se recorreran las fechas de mayor a menor. Si es false
     * se recorreran de menor a mayor
     * @param {ListaEstaciones}[estacionesAMostrar] Lista de estaciones ordenadas y filtradas
     */
    mapXEstacionesYFechasPorIdVar(
        idVar,
        mapFunction,
        ordenInversoFechas,
        estacionesAMostrar
    ) {
        let iVar = this.variables.indexOf(idVar)
        this.mapXEstacionesYFechas(
            iVar,
            mapFunction,
            ordenInversoFechas,
            estacionesAMostrar
        )
    }

    /**
     * Invoca la funcion mapFunction para cada dato de la variable dada por su indice.
     * La fucion es invocada para cada estacion, y dentro de cada estacion para cada fecha.
     * Si se pasa una lista de estaciones en estacionesAMostrar solo se invocara para dichas estaciones
     * y se tomara el orden de dicha lista.
     *
     * @param {number} iVar
     * @param {requestCallback} mapFunction
     * @param {boolean} [ordenInversoFechas=false] Si es true se recorreran las fechas de mayor a menor. Si es false
     * se recorreran de menor a mayor
     * @param {ListaEstaciones}[estacionesAMostrar] Lista de estaciones ordenadas y filtradas
     */
    mapXEstacionesYFechas(
        iVar,
        mapFunction,
        ordenInversoFechas,
        estacionesAMostrar
    ) {
        if (iVar >= 0) {
            let x = 0
            if (!estacionesAMostrar) estacionesAMostrar = this.estaciones
            estacionesAMostrar.list.forEach((estacion) => {
                let iEstacion = this.estaciones.indexOf(estacion.id)
                let datosVarEstac = this.getDatos(iVar, iEstacion)
                for (let y = 0; y < datosVarEstac.length; y++) {
                    let iFecha = ordenInversoFechas
                        ? datosVarEstac.length - 1 - y
                        : y
                    mapFunction(x, y, this.getDato(iVar, iEstacion, iFecha))
                }
                x++
            })
        }
    }

    /**
     * Invoca la funcion mapFunction para cada dato de la fecha dada por su indice.
     * La fucion es invocada para cada estacion, y dentro de cada estacion para cada variable.
     * Las variables consideradas son solo aquellas cuya periodicidad incluye a la fecha dada por su indice.
     * Si se pasa una lista de estaciones en estacionesAMostrar solo se invocara para dichas estaciones
     * y se tomara el orden de dicha lista.
     * Si se pasa una lista de variables en variablesAMostrar solo se invocara para dichas variables
     * y se tomara el orden de dicha lista.
     *
     * @param {number} iFechaGral Indice de la fecha en el array general de fechas
     * @param {requestCallback} mapFunction funcion a invocar
     * @param {ListaEstaciones}[estacionesAMostrar] Lista de estaciones ordenadas y filtradas
     * @param {ListaVariables}[variablesAMostrar] Lista de variables ordenadas y filtradas
     */
    mapXVariablesYEstaciones(
        iFechaGral,
        mapFunction,
        estacionesAMostrar,
        variablesAMostrar
    ) {
        if (this.iFechaGralValido(iFechaGral)) {
            if (!variablesAMostrar) variablesAMostrar = this.variables
            if (!estacionesAMostrar) estacionesAMostrar = this.estaciones

            variablesAMostrar.list.forEach((variable, x) => {
                let iVar = this.variables.indexOf(variable.id)
                if (iVar >= 0) {
                    let iFechaVar = this.getIFechaDeIFechaGral(iVar, iFechaGral)
                    if (iFechaVar >= 0) {
                        let y = 0
                        estacionesAMostrar.forEach((estacion) => {
                            let iEstacion = this.estaciones.indexOf(estacion.id)
                            mapFunction(
                                x,
                                y,
                                this.getDato(iVar, iEstacion, iFechaVar)
                            )
                            y++
                        })
                    }
                }
            })
        }
        return []
    }

    /**
     * Invoca la funcion mapFunction para cada dato de la estacion dada por su id.
     * La fucion es invocada para cada variable, y dentro de cada variable para cada fecha.
     * El orden de invocacion sobre las variables se tomara del definido, sino se invocara sin
     * orden especifico.
     *
     * @param {number} idEstacion
     * @param {requestCallback} mapFunction
     * @param {boolean} [ordenInversoFechas=false] Si es true se recorreran las fechas de mayor a menor. Si es false
     * se recorreran de menor a mayor
     * @param {ListaVariables}[variablesAMostrar] Lista de variables ordenadas y filtradas
     *
     */
    mapXVariablesYFechasPorIdEstacion(
        idEstacion,
        mapFunction,
        ordenInversoFechas,
        variablesAMostrar
    ) {
        let iEstacion = this.estaciones.indexOf(idEstacion)
        this.mapXVariablesYFechas(
            iEstacion,
            mapFunction,
            ordenInversoFechas,
            variablesAMostrar
        )
    }

    /**
     * Invoca la funcion mapFunction para cada dato de la estacion dada por su indice.
     * La fucion es invocada para cada variable, y dentro de cada variable para cada fecha.
     * El orden de invocacion sobre las variables se tomara del definido, sino se invocara sin
     * orden especifico.
     *
     * @param {number} iEstacion
     * @param {requestCallback} mapFunction
     * @param {boolean} [ordenInversoFechas=false] Si es true se recorreran las fechas de mayor a menor. Si es false
     * se recorreran de menor a mayor
     * @param {ListaVariables}[variablesAMostrar] Lista de variables ordenadas y filtradas
     *
     */
    mapXVariablesYFechas(
        iEstacion,
        mapFunction,
        ordenInversoFechas,
        variablesAMostrar
    ) {
        if (this.iEstacionValido(iEstacion)) {
            if (!variablesAMostrar) variablesAMostrar = this.variables
            variablesAMostrar.list.forEach((variable, x) => {
                let iVariable = this.variables.indexOf(variable.id)
                let datosVarEstac = this.getDatos(iVariable, iEstacion)
                for (let y = 0; y < datosVarEstac.length; y++) {
                    let iFecha = ordenInversoFechas
                        ? datosVarEstac.length - 1 - y
                        : y
                    mapFunction(
                        x,
                        y,
                        this.getDato(iVariable, iEstacion, iFecha)
                    )
                }
            })
        }
    }

    /**
     * Invoca la funcion dada para todos los datos de las estaciones en la fecha dada por su indice general
     * y la variable dada por su indice.
     * Las variables consideradas son solo aquellas cuya periodicidad incluye a la fecha dada por su indice.
     * Si se pasa una lista de estaciones en estacionesAMostrar solo se invocara para dichas estaciones
     * y se tomara el orden de dicha lista.
     *
     * @param {*} datosVars
     * @param {*} iVar
     * @param {*} indexFecha
     * @param {*} mapFunction
     * @param {boolean} [ordenInversoFechas=false] Si es true se recorreran las fechas de mayor a menor. Si es false
     * se recorreran de menor a mayor
     * @param {ListaEstaciones}[estacionesAMostrar] Lista de estaciones ordenadas y filtradas
     */
    mapXEstaciones(
        iVar,
        iFechaGral,
        mapFunction,
        ordenInversoFechas,
        estacionesAMostrar
    ) {
        if (!estacionesAMostrar) estacionesAMostrar = this.estaciones
        let iFecha = this.getIFechaDeIFechaGral(iVar, iFechaGral)
        if (iFecha >= 0) {
            estacionesAMostrar.list.forEach((estacion) => {
                let iEstacion = this.estaciones.indexOf(estacion.id)
                if (iEstacion >= 0) {
                    let iFechaAux = ordenInversoFechas
                        ? this.observaciones[iVar].iFechas.length - 1 - iFecha
                        : iFecha
                    mapFunction(
                        iEstacion,
                        this.getDato(iVar, iEstacion, iFechaAux)
                    )
                }
            })
        }
    }

    // ---------------------------
    // ----- Series de datos -----
    // ---------------------------

    /**
     * Retorna un array con todas las fechas (tipo Momement) correspondientes a la variable dada por
     * su índice.
     *
     * @param {number} iVar  Índice de la variable
     * @return {Moment[]} - Array de fechas de tipo Moment de la variable dada
     */
    fechasMoment(iVar) {
        if (this.iVarValido(iVar)) {
            let res = []
            this.observaciones[iVar].iFechas.forEach((iFecha) => {
                res.push(DateUtils.momentDate(this.fechas[iFecha]))
            })
            return res
        }
        return []
    }

    /**
     * Retorna una serie de objetos con atributos x e y. Cada objeto contendra una fecha y un dato
     * correspondientes a la variable y estacion dados por sus indices.
     * Las fechas (tipo Moment) en los atributos x y los datos en los atributos y.
     * Opcionalmente puede pasarse una funcion que se aplicara a cada fecha y su resultado se
     * almacenara en x.
     *
     * @param {*} idVar - Índice de la variable
     * @param {*} idEstacion - Índice de la estación
     * @param {*} fnDate - (opcional) Funcion que se aplicará a cada fecha y cuyo resultado se
     *  almacenara en el valor x. El objetivo de esta función es proporcionar una forma fácil
     *  de dar formato a la fecha de forma flexible.
     */
    serieXFechasYDatos(iVar, iEstacion, fnDate, mapCodigoPorValor) {
        if (this.iVarValido(iVar) && this.iEstacionValido(iEstacion)) {
            let fechas = this.getArrayStrFechasDeVar(this.getIdVar(iVar))
            return this.observaciones[iVar].datos[iEstacion].map((dato, i) => {
                let fecha = fechas[i]
                if (TypesUtils.notUndefOrNull(mapCodigoPorValor[dato])) {
                    dato = mapCodigoPorValor[dato]
                }
                if (fnDate) fecha = fnDate(fecha)
                return { x: fecha, y: dato }
            })
        } else return []
    }

    /**
     * Retorna una serie de objetos con atributos x e y. Cada objeto contendra una fecha y un dato
     * correspondientes a la variable y estacion dados por sus ids.
     * Las fechas (tipo Moment) en los atributos x y los datos en los atributos y.
     * Opcionalmente puede pasarse una funcion que se aplicara a cada fecha y su resultado se
     * almacenara en x.
     *
     * @param {*} idVar - Id de la variable
     * @param {*} idEstacion - Id de la estacion
     * @param {*} fnDate - (opcional) Funcion que se aplicará a cada fecha y cuyo resultado se
     *  almacenara en el valor x. El objetivo de esta función es proporcionar una forma fácil
     *  de dar formato a la fecha de forma flexible.
     */
    serieXFechasYDatosIds(idVar, idEstacion, fnDate, mapCodigoPorValor) {
        return this.serieXFechasYDatos(
            this.iVar(idVar),
            this.iEstacion(idEstacion),
            fnDate,
            mapCodigoPorValor
        )
    }

    /**
     * Retorna true sii la serie de datos de la estacion y la variable dada tienen algun dato no nulo.
     *
     * @param {*} idVar - Índice de la variable
     * @param {*} idEstacion - Índice de la estación
     */
    tieneDatosNoNulos(idVar, idEstacion) {
        let iVar = this.iVar(idVar)
        let iEstacion = this.iEstacion(idEstacion)
        if (iVar >= 0 && iEstacion >= 0) {
            return this.observaciones[iVar].datos[iEstacion].some((dato) => {
                return !this.esNulo(dato)
            })
        } else return false
    }

    getStrVientoHorario(iFecha, idEstacion) {
        const dirViento = this.getDato(
            this.iVar(metConstants.VAR_ID_DIRVIENTO_1H_INST),
            this.iEstacion(idEstacion),
            iFecha,
            true
        )
        const intViento = this.getDato(
            this.iVar(metConstants.VAR_ID_INTVIENTO_1H_INST),
            this.iEstacion(idEstacion),
            iFecha,
            true
        )
        const intVientoMax = this.getDato(
            this.iVar(metConstants.VAR_ID_INTVIENTO_1H_MAX),
            this.iEstacion(idEstacion),
            iFecha,
            true
        )
        let dato = '-'
        if (dirViento) dato = dirViento
        if (intViento) dato += '/' + intViento
        if (intVientoMax) dato += '/' + intVientoMax
        return dato
    }
}

export { DatosVars as default }
